import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { MarketplaceApp, MarketplaceAppService, VendorService } from '@galaxy/marketplace-apps/v1';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { GalaxyDataSource } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import {
  AccountStatus,
  ListVendorAccountMetricsSortField,
  ListVendorAccountMetricsSortOptions,
  ListVendorResellerMetricsFilters,
  ListVendorResellerMetricsRequest,
  PartnerEngagementApiService,
  SortDirection,
  VendorAccountMetrics,
} from '@vendasta/marketplace-analytics';
import { ItemsApiService, ListOwnedItemsFilterOptions, ListOwnedItemsRequest } from '@vendasta/marketplace-apps';
import { FieldMask } from '@vendasta/marketplace-apps/v1';
import { FilterGroup, Item, MultiSearchSelectFilterControl, SidebarState } from '@vendasta/va-filter2';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest } from 'rxjs';
import { debounceTime, filter, map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { PARTNER_ID, VENDOR_ID } from '../../../tokens';
import { ToolbarService } from '../../toolbar/toolbar.service';
import { AccountsTableDataSource } from './accounts-table.data-source';

export const FilterKeyForResellers = 'reseller';
export const FilterKeyForProducts = 'product';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'accounts-table',
  templateUrl: './accounts-table.component.html',
  styleUrls: ['./accounts-table.component.scss'],
})
export class AccountsTableComponent implements OnInit, OnDestroy {
  columns$$ = new ReplaySubject<GalaxyColumnDef[]>(1);
  columns$ = this.columns$$.asObservable();
  columnsWithReseller: GalaxyColumnDef[] = [
    {
      id: 'account',
      title: 'Account',
      pinned: true,
      sticky: true,
    },
    {
      id: 'status',
      title: 'Status',
    },
    {
      id: 'reseller',
      title: 'Reseller',
    },
    {
      id: 'active-product-count',
      title: 'Active products',
    },
    {
      id: 'created',
      title: 'Since',
    },
  ];

  isVendorDistributed$$ = new ReplaySubject<boolean>(1);

  isSidebarClosed: SidebarState = SidebarState.CLOSED;
  filterGroup: FilterGroup;
  // reseller filter
  resellerFilterControl: MultiSearchSelectFilterControl;
  resellerFilterOptions$$ = new BehaviorSubject<Item[]>([]);
  resellerFilterListCursor = '';
  resellerFilterListHasMore = true;
  resellerFilterListSearchTerm$$ = new BehaviorSubject<string>('');
  resellerFilterListLoadMore$$ = new BehaviorSubject<null>(null);
  // product filter
  productFilterControl: MultiSearchSelectFilterControl;
  productFilterOptions$$ = new BehaviorSubject<Item[]>([]);
  productFilterListCursor = '';
  productFilterListHasMore = true;
  productFilterListSearchTerm$$ = new BehaviorSubject<string>('');
  productFilterListLoadMore$$ = new BehaviorSubject<null>(null);

  pageSizeOptions = [10, 25, 50];
  dataSource: AccountsTableDataSource;
  tableDataSource: GalaxyDataSource<VendorAccountMetrics>;
  PopoverPositions = PopoverPositions;
  productIdToName$: Observable<Map<string, string>>;
  hasProducts$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoaded$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private sortOptions$$ = new BehaviorSubject<ListVendorAccountMetricsSortOptions>(
    new ListVendorAccountMetricsSortOptions({
      sortField: ListVendorAccountMetricsSortField.VENDOR_ACCOUNT_METRICS_SORT_FIELD_UNSPECIFIED,
      sortDirection: SortDirection.DESCENDING,
    }),
  );

  activeAccountStatus = AccountStatus.ACCOUNT_STATUS_ACTIVE_ACCOUNT;

  subscriptions: Subscription[] = [];

  constructor(
    @Inject(PARTNER_ID) private partnerId$: Observable<string>,
    @Inject(VENDOR_ID) private vendorId$: Observable<string>,
    private toolbarService: ToolbarService,
    private engagementService: PartnerEngagementApiService,
    private vendorService: VendorService,
    private marketplaceAppService: MarketplaceAppService,
    private itemsApiService: ItemsApiService,
  ) {
    this.toolbarService.setConfig({ title: 'Accounts', actions: [] });

    this.filterGroup = new FilterGroup('accounts-table');
    this.resellerFilterControl = new MultiSearchSelectFilterControl(
      FilterKeyForResellers,
      FilterKeyForResellers,
      [],
      null,
      {
        appliedValueMapper: (name: string, value: Item) => {
          return { name, label: 'Reseller: ' + value.name };
        },
      },
    );
    this.productFilterControl = new MultiSearchSelectFilterControl(
      FilterKeyForProducts,
      FilterKeyForProducts,
      [],
      null,
      {
        appliedValueMapper: (name: string, value: Item) => {
          return { name, label: 'Active product: ' + value.name };
        },
      },
    );
    this.dataSource = new AccountsTableDataSource(
      this.vendorId$,
      this.engagementService,
      this.sortOptions$$.asObservable(),
      this.filterGroup,
    );

    this.subscriptions.push(
      this.partnerId$
        .pipe(
          take(1),
          switchMap((partnerId) => this.vendorService.isVendorDistributed(partnerId)),
        )
        .subscribe((isVendorDistributed) => {
          this.isVendorDistributed$$.next(!!isVendorDistributed);
          if (isVendorDistributed) {
            this.columns$$.next(this.columnsWithReseller);
            // adds filters only applicable to vendors who have distributed products
            this.filterGroup.addSection('Resellers', [this.resellerFilterControl]);
          } else {
            const columnsWithoutReseller = this.columnsWithReseller.filter((column) => column.id !== 'reseller');
            this.columns$$.next(columnsWithoutReseller);
          }
          // adds the rest of the filters (to keep the order)
          this.filterGroup.addSection('Active products', [this.productFilterControl]);
        }),
    );

    this.productIdToName$ = this.dataSource.activeAppIds$.pipe(
      map((appIds) => Array.from(appIds)),
      filter((appIds: string[]) => appIds.length > 0),
      switchMap((appIds) =>
        this.marketplaceAppService.getMultiApps(appIds, new FieldMask({ paths: ['appId', 'name'] })),
      ),
      map((apps: MarketplaceApp[]) => {
        const productNameMap: Map<string, string> = new Map();
        apps.forEach((app) => {
          productNameMap.set(app.appId, app.name);
        });
        return productNameMap;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tableDataSource = new GalaxyDataSource<VendorAccountMetrics>(this.dataSource);
  }

  ngOnInit(): void {
    // Initial check to determine if vendor has any products
    this.partnerId$
      .pipe(
        take(1),
        switchMap((partnerId) => {
          return this.marketplaceAppService.listApps(1, '', new FieldMask({ paths: ['appId', 'name'] }), partnerId);
        }),
        map((response) => Boolean(response.apps?.length)),
      )
      .subscribe((hasProducts) => {
        this.isLoaded$$.next(true);
        this.hasProducts$$.next(hasProducts);
      });

    // load reseller data based on scrolling or searching
    this.subscriptions.push(
      combineLatest([this.vendorId$, this.resellerFilterListLoadMore$$, this.resellerFilterListSearchTerm$$])
        .pipe(
          debounceTime(250),
          switchMap(([vendorId, , searchTerm]) => {
            return this.engagementService.listVendorResellerMetrics(
              new ListVendorResellerMetricsRequest({
                vendorId: vendorId,
                pageSize: 25,
                cursor: this.resellerFilterListCursor,
                filters: new ListVendorResellerMetricsFilters({ searchTerm: searchTerm }),
              }),
            );
          }),
          withLatestFrom(this.resellerFilterOptions$$),
        )
        .subscribe(([resp, resellerFilterOptions]) => {
          return this.updateResellerFilterOptions(
            resellerFilterOptions,
            resp.vendorResellerMetrics?.map((reseller) => {
              return {
                id: reseller.resellerPartnerId,
                name: `${reseller.resellerName} (${reseller.resellerPartnerId})`,
              };
            }),
            resp.nextCursor,
            resp.hasMore,
          );
        }),
    );

    // load product data based on scrolling or searching
    this.subscriptions.push(
      combineLatest([this.vendorId$, this.productFilterListLoadMore$$, this.productFilterListSearchTerm$$])
        .pipe(
          debounceTime(250),
          switchMap(([vendorId, , searchTerm]) => {
            return this.itemsApiService.listOwnedItems(
              new ListOwnedItemsRequest({
                ownerId: vendorId,
                pageSize: 25,
                cursor: this.productFilterListCursor,
                filterOptions: new ListOwnedItemsFilterOptions({ searchTerm: searchTerm }),
              }),
            );
          }),
          withLatestFrom(this.productFilterOptions$$),
        )
        .subscribe(([resp, productFilterOptions]) => {
          return this.updateProductFilterOptions(
            productFilterOptions,
            resp.items?.map((product) => {
              return { id: product.id, name: product.name };
            }),
            resp.nextCursor,
            resp.hasMore,
          );
        }),
    );

    // listen to the va-filter2 search input controls
    this.subscriptions.push(
      this.resellerFilterControl.searchTerm$$.pipe(map((searchTerm) => searchTerm ?? '')).subscribe((searchTerm) => {
        // ignores any value changes from selecting an option
        if (typeof searchTerm === 'string') {
          this.resellerFilterListCursor = '';
          this.resellerFilterListHasMore = true;
          this.resellerFilterListSearchTerm$$.next(searchTerm);
          this.resellerFilterOptions$$.next([]);
        }
      }),
    );

    // listen to the va-filter2 load more
    this.subscriptions.push(
      this.resellerFilterControl.loadMoreEvent$$.subscribe(() => {
        if (!this.resellerFilterListHasMore) {
          return;
        }
        this.resellerFilterListLoadMore$$.next(null);
      }),
    );

    // listen to the va-filter2 search input controls
    this.subscriptions.push(
      this.productFilterControl.searchTerm$$.pipe(map((searchTerm) => searchTerm ?? '')).subscribe((searchTerm) => {
        // ignores any value changes from selecting an option
        if (typeof searchTerm === 'string') {
          this.productFilterListCursor = '';
          this.productFilterListHasMore = true;
          this.productFilterListSearchTerm$$.next(searchTerm);
          this.productFilterOptions$$.next([]);
        }
      }),
    );

    // listen to the va-filter2 load more
    this.subscriptions.push(
      this.productFilterControl.loadMoreEvent$$.subscribe(() => {
        if (!this.productFilterListHasMore) {
          return;
        }
        this.productFilterListLoadMore$$.next(null);
      }),
    );

    // set up the va-filter2 options and selected options
    this.subscriptions.push(
      combineLatest([this.filterGroup.valueChanges, this.resellerFilterOptions$$, this.productFilterOptions$$])
        .pipe()
        .subscribe(([filterControl, resellerFilterOptions, productFilterOptions]) => {
          this.resellerFilterControl.options$$.next(resellerFilterOptions);
          this.resellerFilterControl.selected$$.next(
            filterControl[FilterKeyForResellers] ? filterControl[FilterKeyForResellers] : [],
          );
          this.productFilterControl.options$$.next(productFilterOptions);
          this.productFilterControl.selected$$.next(
            filterControl[FilterKeyForProducts] ? filterControl[FilterKeyForProducts] : [],
          );
        }),
    );
  }

  handleSortChange(sortEvent: Sort): void {
    let sortField = ListVendorAccountMetricsSortField.VENDOR_ACCOUNT_METRICS_SORT_FIELD_UNSPECIFIED;
    let sortDirection = SortDirection.DESCENDING;
    if (sortEvent.direction) {
      if (sortEvent.active === 'created') {
        sortField = ListVendorAccountMetricsSortField.VENDOR_ACCOUNT_METRICS_SORT_FIELD_CREATED;
      } else if (sortEvent.active === 'account') {
        sortField = ListVendorAccountMetricsSortField.VENDOR_ACCOUNT_METRICS_SORT_FIELD_ACCOUNT_COMPANY_NAME;
      } else if (sortEvent.active === 'reseller') {
        sortField = ListVendorAccountMetricsSortField.VENDOR_ACCOUNT_METRICS_SORT_FIELD_RESELLER_NAME;
      } else if (sortEvent.active === 'active-product-count') {
        sortField = ListVendorAccountMetricsSortField.VENDOR_ACCOUNT_METRICS_SORT_FIELD_TOTAL_ACTIVE_ACTIVATIONS;
      }
    }
    if (sortEvent.direction === 'asc') {
      sortDirection = SortDirection.ASCENDING;
    }
    const sortOptions = new ListVendorAccountMetricsSortOptions({
      sortField: sortField,
      sortDirection: sortDirection,
    });
    this.sortOptions$$.next(sortOptions);
  }

  toggleFilters(): void {
    if (this.isSidebarClosed === SidebarState.OPEN) {
      this.isSidebarClosed = SidebarState.CLOSED;
    } else {
      this.isSidebarClosed = SidebarState.OPEN;
    }
  }

  updateResellerFilterOptions(
    existingSelectableItems: Item[],
    newSelectableItems: Item[],
    cursor: string,
    hasMore: boolean,
  ): void {
    this.resellerFilterOptions$$.next((existingSelectableItems ?? []).concat(newSelectableItems ?? []));
    this.resellerFilterListCursor = cursor;
    this.resellerFilterListHasMore = hasMore;
  }

  updateProductFilterOptions(
    existingSelectableItems: Item[],
    newSelectableItems: Item[],
    cursor: string,
    hasMore: boolean,
  ): void {
    this.productFilterOptions$$.next((existingSelectableItems ?? []).concat(newSelectableItems ?? []));
    this.productFilterListCursor = cursor;
    this.productFilterListHasMore = hasMore;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
